<template>
  <v-container class="my-5">
    <v-layout row wrap class="justify-center">
      <v-flex xs12>
        <v-card-title class="mb-4">
          <v-btn icon @click="$router.go(-1)" class="mr-3">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <span class="headline font-weight-bold">{{ $t('golf_blacklist') }}</span>
          <v-spacer></v-spacer>
        </v-card-title>

        <v-data-table :headers="headers" :items="blacklist" :loading="loading" class="elevation-1"
          :no-data-text="$t('no_records_yet')">
          <template v-slot:item.user_details="{ item }">
            <div class="d-flex align-center">
              <v-avatar size="32" color="primary" class="white--text mr-2">
                <span class="caption">{{ item.user ? (item.user.first_name[0] + item.user.last_name[0]) : '?' }}</span>
              </v-avatar>
              <div>
                <div>{{ item.user ? (item.user.first_name + ' ' + item.user.last_name) : 'Unknown' }}</div>
                <div class="caption grey--text">{{ item.user ? item.user.email : '' }}</div>
              </div>
            </div>
          </template>
          <template v-slot:item.strikes="{ item }">
            <v-chip :color="getStrikeColor(item.strikes)" dark small>
              <v-icon left x-small>mdi-alert-circle</v-icon>
              {{ item.strikes }}/3
            </v-chip>
          </template>
          <template v-slot:item.status="{ item }">
            <v-chip :color="getStatusChipColor(item)" dark small>
              {{ getStatusText(item) }}
            </v-chip>
          </template>
          <template v-slot:item.restricted_until="{ item }">
            {{ formatRestrictedDate(item.restricted_until) }}
          </template>
          <template v-slot:item.actions="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon small class="mr-2" v-bind="attrs" v-on="on" @click="confirmResetStrikes(item)"
                  color="orange">
                  <v-icon small>mdi-refresh</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('reset_strikes') || 'Resetear strikes' }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon small v-bind="attrs" v-on="on" @click="confirmUnban(item)" color="green">
                  <v-icon small>mdi-lock-open-variant</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('remove_from_blacklist') }}</span>
            </v-tooltip>
          </template>
        </v-data-table>
      </v-flex>
    </v-layout>

    <!-- Reset Strikes Confirmation Dialog -->
    <v-dialog v-model="dialogResetStrikes" max-width="500px">
      <v-card>
        <v-card-title class="headline">{{ $t('reset_strikes') }}</v-card-title>
        <v-card-text>
          <p>{{ $t('confirm_reset_strikes') }}</p>
          <p class="caption grey--text">{{ $t('reset_strikes_info') }}</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="closeResetStrikes">{{ $t('cancelar') }}</v-btn>
          <v-btn color="orange darken-1" text @click="resetStrikesConfirm">{{ $t('si') || 'Si' }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Unban Confirmation Dialog -->
    <v-dialog v-model="dialogUnban" max-width="500px">
      <v-card>
        <v-card-title class="headline">{{ $t('remove_from_blacklist') }}</v-card-title>
        <v-card-text>
          <p>{{ $t('confirm_remove_blacklist') }}
          </p>
          <p class="caption grey--text">{{ $t('unban_info') }}</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="closeUnban">{{ $t('cancelar') }}</v-btn>
          <v-btn color="green darken-1" text @click="unbanConfirm">{{ $t('si') || 'Si' }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="5000" top>
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import axios from 'axios'
import store from '@/store'

const API_BASE_URL = 'https://clubtest.intrare.app'

export default {
  name: 'GolfBlacklist',
  data: () => ({
    loading: false,
    dialogResetStrikes: false,
    dialogUnban: false,
    blacklist: [],
    itemToResetStrikes: null,
    itemToUnban: null,
    snackbar: false,
    snackbarText: '',
    snackbarColor: 'success'
  }),

  computed: {
    headers () {
      return [
        { text: this.$t('blocked_user'), value: 'user_details' },
        { text: this.$t('strikes') || 'Strikes', value: 'strikes', align: 'center' },
        { text: this.$t('status') || 'Estado', value: 'status', align: 'center' },
        { text: this.$t('reason'), value: 'reason' },
        { text: this.$t('restricted_until'), value: 'restricted_until' },
        { text: this.$t('acciones'), value: 'actions', sortable: false, align: 'end' }
      ]
    }
  },

  created () {
    this.initialize()
  },

  methods: {
    async initialize () {
      await this.getBlacklist()
    },

    async getBlacklist () {
      this.loading = true
      try {
        const idHousingUnit = store.getters.getProfile.idHousingUnit || localStorage.getItem('idHousingUnit')
        const response = await this.apiCall('get', `/golf-blacklist/?housing_unit=${idHousingUnit}`)
        this.blacklist = response.data
      } catch (error) {
        console.error('Error fetching blacklist:', error)
        this.showSnackbar(this.$t('error_fetching_blacklist'), 'error')
      } finally {
        this.loading = false
      }
    },

    apiCall (method, endpoint, data = null) {
      const config = {
        method,
        url: `${API_BASE_URL}${endpoint}`,
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': this.language,
          'authorization': 'Token ' + store.getters.getToken
        }
      }

      if (data) {
        config.data = data
      }

      return axios(config)
    },

    showSnackbar (text, color) {
      this.snackbarText = text
      this.snackbarColor = color
      this.snackbar = true
    },

    formatRestrictedDate (datetime) {
      if (!datetime) return this.$t('permanent')
      return datetime.split('T')[0]
    },

    getStrikeColor (strikes) {
      if (strikes === 0) return 'green'
      if (strikes === 1) return 'orange'
      if (strikes === 2) return 'deep-orange'
      return 'red'
    },

    getStatusText (item) {
      if (item.strikes >= 3 && item.is_active) {
        return this.$t('banned') || 'Banned'
      }
      return this.$t('active') || 'Active'
    },

    getStatusChipColor (item) {
      if (item.strikes >= 3 && item.is_active) return 'red'
      return 'green'
    },

    confirmResetStrikes (item) {
      this.itemToResetStrikes = item
      this.dialogResetStrikes = true
    },

    async resetStrikesConfirm () {
      if (this.itemToResetStrikes) {
        try {
          await this.apiCall('post', `/golf-blacklist/${this.itemToResetStrikes.id}/reset_strikes/`)
          this.showSnackbar(this.$t('strikes_reset_success') || 'Strikes reseteados exitosamente', 'success')
          await this.getBlacklist()
        } catch (error) {
          console.error('Error resetting strikes:', error)
          const errorMsg = error.response?.data?.detail || error.response?.data?.message || this.$t('error_resetting_strikes')
          this.showSnackbar(errorMsg, 'error')
        } finally {
          this.closeResetStrikes()
        }
      }
    },

    closeResetStrikes () {
      this.dialogResetStrikes = false
      this.$nextTick(() => {
        this.itemToResetStrikes = null
      })
    },

    confirmUnban (item) {
      this.itemToUnban = item
      this.dialogUnban = true
    },

    async unbanConfirm () {
      if (this.itemToUnban) {
        try {
          await this.apiCall('post', `/golf-blacklist/${this.itemToUnban.id}/unban/`)
          this.showSnackbar(this.$t('removed_from_blacklist_success'), 'success')
          await this.getBlacklist()
        } catch (error) {
          console.error('Error removing from blacklist:', error)
          const errorMsg = error.response?.data?.detail || error.response?.data?.message || this.$t('error_removing_blacklist')
          this.showSnackbar(errorMsg, 'error')
        } finally {
          this.closeUnban()
        }
      }
    },

    closeUnban () {
      this.dialogUnban = false
      this.$nextTick(() => {
        this.itemToUnban = null
      })
    }
  }
}
</script>
